<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import type { FieldConfig } from '@/types'

  const props = defineProps<{
    field: FieldConfig,
    formData?: Record<string, any>,
  }>()

  const emit = defineEmits<{
    (e: 'update-field', key: string, value: any): void,

  }>()

  const isOpen = ref(false)
  const selectEl = ref<HTMLDivElement | null>(null)

  const selectedLabel = ref('')
  const selectedValue = ref(props.formData?.[props.field.key] ?? null)

  const updateSelectedLabel = (val?: any) => {
    const raw = val ?? props.formData?.[props.field.key]
    const opt = props.field.options?.find(o => o.value === raw)
    selectedLabel.value = opt?.label || (props.field.options?.[0]?.label ?? '')
  }

  const updateSelectedValue = (val?: any) => {
    selectedValue.value = val ?? props.formData?.[props.field.key] ?? null
  }

  const toggleDropdown = () => {
    isOpen.value = !isOpen.value
  }

  const selectOption = (value: any) => {
    emit('update-field', props.field.key as string, value)
    updateSelectedLabel(value)
    updateSelectedValue(value)
    isOpen.value = false
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (selectEl.value && !selectEl.value.contains(event.target as Node)) {
      isOpen.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    updateSelectedLabel()
    updateSelectedValue()
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  // watch for initial and subsequent prop changes
  watch(
    () => props.formData?.[props.field.key],
    (newVal) => {
      updateSelectedValue(newVal)
      updateSelectedLabel(newVal)
    },
    { immediate: true },
  )
</script>

<template>
  <div
    ref="selectEl"
    class="relative w-full">
    <div
      class="flex items-center w-full px-4 sm:px-5 py-2 sm:py-3 text-sm text-base font-medium text-gray-600 bg-white border-[1.5px] border-primary-700 cursor-pointer rounded-xl"
      :class="{ 'rounded-b-none  border-b-0 border-x-[1.5px] ': isOpen }"
      @click="toggleDropdown">
      <span class="truncate flex-1">
        {{ selectedLabel }}
      </span>
      <svg
        class="size-4 ml-2 text-primary-700 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <!-- Dropdown options -->
    <div
      v-if="isOpen"
      class="absolute top-full left-0 w-full bg-white border-[1.5px] border-t-0 border-primary-700 z-10 max-h-60 overflow-y-auto rounded-b-xl pb-2">
      <div class="px-4">
        <hr>
      </div>
      <div
        v-for="option in field.options || []"
        :key="option.value"
        class="px-4 sm:px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer"
        :class="{ 'bg-primary-50 text-primary-700': selectedValue === option.value }"
        @click="selectOption(option.value)">
        {{ option.label }}
      </div>
    </div>

    <!-- Hidden native select for accessibility -->
    <select
      :value="props.formData?.[field.key as string]"
      class="absolute opacity-0 pointer-events-none size-0"
      @change="e => emit('update-field', field.key as string, (e.target as HTMLSelectElement).value)">
      <option
        v-for="option in field.options || []"
        :key="option.value"
        :value="option.value">
        {{ option.label }}
      </option>
    </select>
  </div>
</template>

